import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/design-system";
import { readConsent, writeConsent, acceptAll, rejectAll } from "@/lib/consent";

/**
 * EU-compliant cookie banner.
 *  - Shown until an explicit choice is recorded (localStorage `sll_consent`).
 *  - "Accept all" and "Reject non-essential" are equally prominent (both Buttons,
 *    same weight, side-by-side) — no dark-pattern styling.
 *  - "Customise" opens per-category toggles; essential is always on and locked.
 *  - The banner is dismissable only via a real choice; there is no [X] close.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const onOpen = () => {
      setPrefs({ analytics: false, marketing: false });
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("sll:cookie-settings", onOpen);
    return () => window.removeEventListener("sll:cookie-settings", onOpen);
  }, []);

  if (!visible) return null;

  const finish = () => { setVisible(false); setShowPrefs(false); };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-4xl rounded-2xl bg-white text-[#0B1F3A] shadow-2xl border border-[#0B1F3A]/10 overflow-hidden">
        <div className="p-5 sm:p-6">
          <h2 className="font-[Montserrat] font-bold text-lg sm:text-xl">We respect your privacy.</h2>
          <p className="text-sm text-[#0B1F3A]/80 mt-2">
            We use <b>strictly necessary</b> cookies to run this app (log you in, keep you signed
            in, and protect the site from cross-site request forgery). With your permission we
            may also use optional cookies for analytics and marketing. Read the full{" "}
            <Link to="/legal/cookies" className="text-[#2D7FF9] font-semibold hover:underline" data-testid="cookie-banner-policy-link">
              cookie policy
            </Link>. You can change your choice at any time in <b>Settings → Cookie preferences</b>.
          </p>

          {showPrefs && (
            <div className="mt-4 space-y-3 rounded-lg bg-[#E8F1FF]/40 p-4 border border-[#0B1F3A]/10" data-testid="cookie-prefs">
              <Row label="Strictly necessary" desc="Auth, CSRF, session. Cannot be disabled." locked checked />
              <Row
                label="Analytics"
                desc="Anonymised usage stats so we know which parts of the app are useful. Off by default."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                testId="cookie-toggle-analytics"
              />
              <Row
                label="Marketing"
                desc="Personalised messages about launches, referrals, and community wins. Off by default."
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                testId="cookie-toggle-marketing"
              />
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
            {showPrefs ? (
              <>
                <Button variant="primary" onClick={() => { writeConsent(prefs); finish(); }} data-testid="cookie-save-prefs">
                  Save my choices
                </Button>
                <Button variant="ghost" onClick={() => setShowPrefs(false)} data-testid="cookie-back">Back</Button>
              </>
            ) : (
              <>
                <Button variant="primary" onClick={() => { acceptAll(); finish(); }} data-testid="cookie-accept">
                  Accept all
                </Button>
                <Button variant="primary" onClick={() => { rejectAll(); finish(); }} data-testid="cookie-reject">
                  Reject non-essential
                </Button>
                <Button variant="ghost" onClick={() => setShowPrefs(true)} data-testid="cookie-customise">
                  Customise
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, locked = false, testId }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={locked ? undefined : () => onChange(!checked)}
        data-testid={testId}
        disabled={locked}
        className={`mt-0.5 shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          checked ? "bg-[#2D7FF9]" : "bg-[#0B1F3A]/25"
        } ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-4" : "translate-x-1"
          }`}
        />
      </button>
      <div className="min-w-0">
        <div className="text-sm font-semibold flex items-center gap-2">
          {label} {locked && <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">Always on</span>}
        </div>
        <div className="text-xs text-[#0B1F3A]/70">{desc}</div>
      </div>
    </div>
  );
}
